<template>
  <div
    class="booking-card"
    :class="{
      '-loading': loading,
      '-playing': bookingStationTime._playing,
      '-paused': bookingStationTime.pausedAt,
      '-conflict': bookingStationTime._conflict,
      '-new': bookingStationTime._new,
      '-small': small,
      '-aboveConflict': aboveConflict,
      '-ended': ended
    }"
    :style="{ left, top, width, height }">
    <div class="earmark"></div>
    <h1 class="title f5">{{ booking.title }}</h1>
    <h2 class="time f8">{{ fromToTime }}</h2>
    <div class="conflictstatus f6" v-if="bookingStationTime._conflict">
      <span v-if="bookingStationTime._conflict && !aboveConflict">{{ $t('Conflict') }}</span>
      <span v-if="aboveConflict">{{ $t('Conflict') }} ↓</span>
    </div>
    <div class="playingstatus f6">
      <p>
      bookingcard
    </p>
      <span class="notplaying" v-if="isNetworkAvailable && !bookingStationTime._playing && !ended">{{ $t('Click To Start') }}</span>
      <span v-else-if="bookingStationTime.pausedAt">{{ $t('Paused') }}</span>
      <span v-else-if="bookingStationTime._playing">{{ $t('Playing') }}</span>
    </div>
    <UIIcon v-if="booking.notes" name="notes" class="notes" :title="booking.notes" />
    <span class="notpaid f6">
      <span v-if="amountDue && !isOrganizationEducation">{{ $t('Not Paid') }}</span>
      <span class="missing-waivers" v-if="missingWaivers">{{ $t('Missing Waivers') }}</span>
    </span>
    <Loading class="loadinganimation" v-if="loading" />
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import OverviewHelper from '@/location/overview/OverviewHelper';
import moment from 'moment-timezone';
import Loading from '@/core/shared/components/Loading';
import DateHelper from '@/core/shared/helpers/DateHelper';
import UIIcon from '@/core/shared/components/ui/UIIcon';

@Component({
  props: {
    booking: {
      type: Object,
      required: true,
    },
    stationId: {
      type: String,
      required: true,
    },
    vertical: {
      type: Boolean,
      default: false,
    },
    twentyFourHourTime: {
      type: Boolean,
      required: true,
    },
    calendarDate: {
      type: String,
      required: true,
    },
    isNetworkAvailable: {
      type: Boolean,
      required: true,
    },
    isOrganizationEducation: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Loading,
    UIIcon,
  },
})
export default class BookingCard extends Vue {
  get loading () {
    return this.booking.id.includes('loading');
  }

  get leftPercent () {
    if (this.bookingStationTime.startedAt) {
      return OverviewHelper.timeToPercentThroughDay(moment(this.bookingStationTime.startedAt), this.calendarDate);
    } else {
      return OverviewHelper.timeToPercentThroughDay(moment(this.booking.startTime), this.calendarDate);
    }
  }

  get bookingStationTime () {
    return this.booking.bookingStationTimes.find(bookingStationTime => bookingStationTime.station.id === this.stationId);
  }

  get rightPercent () {
    if (
      DateHelper.dateMatch(this.booking.startTime, this.bookingStationTime.endTime) ||
      DateHelper.dateMatch(this.bookingStationTime.endTime, this.calendarDate)
    ) {
      return OverviewHelper.timeToPercentThroughDay(moment(this.bookingStationTime.endTime));
    } else {
      return 1.001;
    }
  }

  get top () {
    return (this.vertical)
      ? `${100 * this.leftPercent}%`
      : '1rem';
  }

  get left () {
    return (this.vertical)
      ? '0'
      : `${100 * this.leftPercent}%`;
  }

  get width () {
    if (this.vertical) {
      return 'calc(100% - 10rem)';
    } else {
      if (this.rightPercent < this.leftPercent) return '10000px';
      return `${100 * (this.rightPercent - this.leftPercent)}%`;
    }
  }

  get height () {
    return (this.vertical)
      ? `${100 * (this.rightPercent - this.leftPercent)}%`
      : '11rem';
  }

  get small () {
    return parseInt(this.width) < 99;
  }

  get placeholderImage () {
    return this.booking.title.substring(0, 2);
  }

  get aboveConflict () {
    if (this.bookingStationTime._conflict) {
      const start = moment(this.booking.startTime);
      const conflict = moment(this.bookingStationTime._conflict);
      return start.isBefore(conflict) || start.isSame(conflict);
    }
  }

  get fromToTime () {
    if (this.twentyFourHourTime) {
      return moment(this.booking.startTime).format('H:mm') + ' - ' + moment(this.bookingStationTime.endTime).format('H:mm');
    } else {
      return moment(this.booking.startTime).format('h:mm a') + ' - ' + moment(this.bookingStationTime.endTime).format('h:mm a');
    }
  }

  get amountDue () {
    return this.booking.bookingStationTimes.reduce((price, bookingStationTime) => {
      return price + bookingStationTime.amountDue;
    }, 0);
  }

  get ended () {
    return moment(this.bookingStationTime.endTime).diff(moment(this.currentTime)) < 0 && this.bookingStationTime.startedAt;
  }

  get missingWaivers () {
    if (this.booking.location.waiversRequired) {
      return this.booking.numPlayers - this.booking.waiverNumSigned - this.booking.waiverNumMinors > 0;
    }
    return false;
  }
}
</script>

<style scoped lang="postcss">
@import 'core/shared/styles';

.booking-card {
  position: absolute;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: var(--radius);
  cursor: pointer;
  overflow: hidden;
  transition: background-color 0.3s ease;
  padding: 0 var(--spacingSm);
  padding-top: 1.8rem;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadowSm);
  z-index: 1;

  & .title {
    font-size: 1.4rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin: 0;
    line-height: 1;
    margin-bottom: var(--spacingXS);
  }

  & .time {
    color: var(--colorManatee);
    line-height: 1.2;
    margin: 0;
    margin-bottom: var(--spacingXS);
  }

  & ._conflictstatus {
    margin: 0;
    line-height: 1;
    margin-bottom: var(--spacingXS);
    color: var(--colorSBRedFlat);
  }

  & .playingstatus {
    margin: 0;
    line-height: 1;
  }

  & .notpaid {
    position: absolute;
    top: var(--spacingXS);
    right: var(--spacingXS);
    color: var(--colorSBRedFlat);
    line-height: 1;
    text-align: right;

    & .missing-waivers::before {
      content: ' ';
      display: block;
    }
  }

  & .earmark {
    position: absolute;
    top: -1rem;
    left: -0.2rem;
    width: 0;
    height: 0;
    border-top: 1.5rem solid transparent;
    border-bottom: 1.5rem solid transparent;
    border-right: 1.5rem solid var(--colorTransparent);
    opacity: 0;
    transform: rotate(45deg);
    border-radius: 0.4rem;
    transition: opacity 0.3s ease;
  }

  & .notes {
    position: absolute;
    bottom: var(--spacingXS);
    left: var(--spacingXS);
    fill: var(--colorSBRedFlat);
    width: 1.5rem;
    height: 1.5rem;
    cursor: pointer;
  }

  &.-loading {
    padding-top: 0;
    opacity: 0.9;

    & > * {
      display: none;
    }

    & .loadinganimation {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }
  }

  &.-new {
    & .earmark {
      border-right: 1.5rem solid var(--colorInfoFlat);
      opacity: 1;
    }

    & .playingstatus {
      color: var(--colorInfoFlat);
    }
  }

  &.-playing {
    & .earmark {
      border-right: 1.5rem solid var(--colorSuccessFlat);
      opacity: 1;
    }

    & .playingstatus {
      color: var(--colorSuccessFlat);
    }
  }

  &.-paused {
    & .earmark {
      border-right: 1.5rem solid var(--colorErrorFlat);
      opacity: 1;
    }

    & .playingstatus {
      color: var(--colorErrorFlat);
    }
  }

  &.-conflict {
    & .earmark {
      border-right: 1.5rem solid var(--colorSBRedFlat);
      opacity: 1;
    }
  }

  &.-ended {
    background-color: var(--colorCasper);

    & .earmark {
      display: none;
    }

    & .playingstatus {
      display: none;
    }
  }

  &.-aboveConflict {
    box-shadow: var(--shadowMd);
    height: 10rem !important;
    opacity: 0.9;
    z-index: 2 !important;
  }

  &.-small {
    padding-left: var(--spacingXS);
    padding-right: var(--spacingXS);

    & .time, & .notpaid, & .playingstatus, & .-conflictstatus {
      font-size: 0.7rem;
    }
  }
}
</style>
